import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, ExternalLink, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

import { getAllPublishedInsights } from '@/services/insight';
import { toast } from 'sonner';
import { pagination, marketInsights } from '@/constants';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { encodeS3Url } from '@/components/encodeS3Url';
import { capitalizeWords } from '@/helper';
import SEO from '@/components/seo/seo';

const MarketInsights = () => {
  const [filter, setFilter] = useState('All');
  const [reports, setReports] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const fetchReports = async (page = pagination.page) => {
    setIsLoading(true);
    try {
      const queryParams: any = {
        page: page,
        limit: pagination.limit,
      };

      if (filter !== 'All') {
        queryParams.category = filter;
      }

      const { data, pagination: paginationData } = await getAllPublishedInsights(queryParams);

      if (Array.isArray(data)) {
        setReports(data);
        if (paginationData) {
          setTotalPages(paginationData.totalPages);
        }
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.error("Failed to fetch market insights:", error);
      toast.error("Failed to load market insights");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [filter]);

  // Combine "All" with imported categories
  const categories = [{ label: "All", value: "All" }, ...marketInsights];

  const handleFilterChange = (category: string) => {
    setFilter(category); // This triggers useEffect
  }

  return (
    <>
      <SEO
        title="Market Insights | Urban Oasis UAE"
        description="Expert analysis and comprehensive reports on Dubai real estate market to help you make informed investment decisions."
        url="/market-insights"
        image="/image/logo.png"
        keywords="dubai real estate, urban oasis"
      />
      <div className="flex-grow pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Insights</h1>
            <p className="text-muted-foreground max-w-2xl">
              Expert analysis and comprehensive reports on Dubai real estate market to help you make informed investment decisions.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div className="flex gap-3 flex-wrap">
              {categories.map(category => (
                <Badge
                  key={category.value}
                  className="cursor-pointer"
                  variant={filter === category.value ? "default" : "outline"}
                  onClick={() => handleFilterChange(category.value)}
                >
                  {category.label}
                </Badge>
              ))}
            </div>
          </div>

          {isLoading ? (
            <>
              {/* Featured Report Skeleton */}
              <Card className="mb-12 overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <Skeleton className="h-[500px] w-full" />
                  <div className="p-6 md:p-8 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Skeleton className="h-6 w-20" />
                        <Skeleton className="h-6 w-24" />
                      </div>
                      <Skeleton className="h-8 w-3/4 mb-3" />
                      <div className="space-y-2 mb-6">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                      <div className="flex items-center gap-4 mb-6">
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="h-10 w-32" />
                      <Skeleton className="h-10 w-36" />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Reports Grid Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden flex flex-col h-full">
                    <Skeleton className="aspect-[4/3] w-full" />
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Skeleton className="h-4 w-20" />
                      </div>
                      <Skeleton className="h-6 w-3/4" />
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <div className="space-y-2 mb-4">
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Skeleton className="h-9 w-24" />
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </>
          ) : reports.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-medium mb-2">No reports available</h3>
              <p className="text-muted-foreground">Check back later for the latest market insights.</p>
            </div>
          ) : (
            <>
              {/* Featured Report - Taking the first one */}
              {reports.length > 0 && (
                <Card className="mb-12 overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="h-[500px]">
                      <img
                        src={reports[0].featuredImage ? encodeS3Url(reports[0].featuredImage) : '/placeholder.svg'}
                        alt={reports[0].title}
                        className="w-full h-full object-cover min-h-[500px]"
                      />
                    </div>
                    <div className="p-6 md:p-8 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge>Featured</Badge>
                          <Badge variant="outline">{reports[0].category || 'General'}</Badge>
                        </div>
                        <CardTitle className="text-2xl md:text-3xl mb-3">
                          {capitalizeWords(reports[0].title)}
                        </CardTitle>
                        <CardContent className="text-muted-foreground p-0 mb-6 line-clamp-3">
                          {reports[0].description}
                        </CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(reports[0].createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                        </div>
                      </div>
                      <CardFooter className="p-0 flex gap-3">
                        <Button asChild>
                          <Link to={`/market-insights/${reports[0].slug}`}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View Report
                          </Link>
                        </Button>
                        <Button variant="outline" disabled={!reports[0].pdfFile} asChild={!!reports[0].pdfFile}>
                          {reports[0].pdfFile ? (
                            <a href={encodeS3Url(reports[0].pdfFile)} download target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </a>
                          ) : (
                            <span>
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </span>
                          )}
                        </Button>
                      </CardFooter>
                    </div>
                  </div>
                </Card>
              )}

              {/* Reports Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reports.slice(1).map((report) => (
                  <Card key={report._id} className="overflow-hidden flex flex-col h-full transition-all hover:shadow-lg">
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                      <img
                        src={report.featuredImage ? encodeS3Url(report.featuredImage) : '/placeholder.svg'}
                        alt={report.title}
                        className="w-full h-full object-cover"
                      />
                      <Badge className="absolute top-3 left-3">{report.category || 'General'}</Badge>
                    </div>
                    <CardHeader>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(report.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <CardTitle className="line-clamp-2">{capitalizeWords(report.title)}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-muted-foreground flex-grow">
                      <p className="line-clamp-3 mb-4">{report.description}</p>
                    </CardContent>
                    <CardFooter className="flex justify-between mt-auto pt-0">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/market-insights/${report.slug}`}>
                            Read More
                          </Link>
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Pagination Control - Kept mounted to preserve state */}
          {(!isLoading && reports.length === 0) ? null : (
            <div className={isLoading ? "hidden" : "mt-8"}>
              <PaginationControl
                totalPages={totalPages}
                onPageChange={fetchReports}
                key={filter}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MarketInsights;
